import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Factory,
  Truck,
  Headphones,
} from 'lucide-react';
import {
  homeScrollItem,
  homeScrollStagger,
  homeScrollViewport,
} from '../../utils/homeScrollMotion';
import styles from './WhyChooseUs.module.css';

const HERO_IMAGE = '/images/banner1 (2).jpeg';

const reasons = [
  {
    icon: ShieldCheck,
    title: 'Premium quality',
    detail: 'Food-grade materials with batch-wise QC on every run.',
    accent: 'green',
  },
  {
    icon: Factory,
    title: '8+ years expertise',
    detail: 'Manufacturing & supply from Meerut since 2018.',
    accent: 'warm',
  },
  {
    icon: Truck,
    title: 'Pan-India dispatch',
    detail: 'Repeat bulk schedules for distributors & plants.',
    accent: 'warm',
  },
  {
    icon: Headphones,
    title: 'Dedicated B2B support',
    detail: 'Specs, timelines & orders aligned to your line.',
    accent: 'green',
  },
];

const WhyChooseUs = () => (
  <section className={`${styles.section} home-bg-linen`} aria-labelledby="why-choose-heading">
    <div className={styles.bgDecor} aria-hidden="true">
      <span className={styles.orbGreen} />
      <span className={styles.orbOrange} />
    </div>

    <div className={styles.container}>
      <motion.div
        className={styles.frame}
        variants={homeScrollItem}
        initial="hidden"
        whileInView="visible"
        viewport={homeScrollViewport}
      >
        <span className={styles.cutCorner} aria-hidden="true" />

        <div className={styles.inner}>
          <div className={styles.media}>
            <img
              src={HERO_IMAGE}
              alt="Greeneri paper manufacturing facility"
              loading="lazy"
            />
            <span className={styles.mediaFade} aria-hidden="true" />
            <p className={styles.mediaCaption}>
              <span>8+ years</span>
              <span className={styles.captionDot} aria-hidden="true">·</span>
              <span>Meerut, India</span>
            </p>
          </div>

          <div className={styles.copy}>
            <motion.header
              className={styles.header}
              variants={homeScrollItem}
              initial="hidden"
              whileInView="visible"
              viewport={homeScrollViewport}
            >
              <p className={styles.eyebrow}>Why partner with us</p>
              <h2 id="why-choose-heading" className={styles.title}>
                Why Choose <span className={styles.highlight}>Greeneri Papers?</span>
              </h2>
              <p className={styles.subtitle}>
                Your trusted partner for premium paper packaging — quality, scale & reliable supply
                across India. We work with QSR chains, cafés, converters, and distributors who need
                paper they can spec once and reorder with confidence.
              </p>
            </motion.header>

            <p className={styles.lead}>
              From cup sleeves and food wrap to PE coated rolls and jumbo stock — we deliver consistent
              GSM, clean finish, and dispatch you can plan around. Whether you run a single outlet or a
              multi-city supply line, our Meerut plant keeps batch quality steady and timelines clear
              from quote to dispatch.
            </p>

            <p className={styles.pointsHead}>What you get</p>

            <motion.ul
              className={styles.points}
              variants={homeScrollStagger}
              initial="hidden"
              whileInView="visible"
              viewport={homeScrollViewport}
            >
              {reasons.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.li
                    key={item.title}
                    className={styles.point}
                    data-accent={item.accent}
                    data-shift={index % 2 === 0 ? 'left' : 'right'}
                    variants={homeScrollItem}
                    whileHover={{ x: index % 2 === 0 ? 4 : -4, transition: { duration: 0.2 } }}
                  >
                    <span className={styles.pointMark}>
                      <Icon size={17} strokeWidth={2.35} aria-hidden="true" />
                    </span>
                    <div className={styles.pointBody}>
                      <strong>{item.title}</strong>
                      <span>{item.detail}</span>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default WhyChooseUs;
